import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { redirectFor } from "./Login";

// Phase A.2 — Cloudflare Turnstile site key. When unset, widget is hidden
// and the backend gracefully degrades to accept-all (preview/dev/CI).
const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "driver" ? "driver" : "passenger";

  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: initialRole,
    vehicle_make: "",
    vehicle_plate: "",
  });
  const [loading, setLoading] = useState(false);
  // Turnstile state — only meaningful when a site key is configured.
  // When the widget solves, Cloudflare hands us a token we forward to the
  // backend; the backend validates it against /turnstile/v0/siteverify.
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileMount = useRef(null);
  const turnstileWidgetId = useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Lazy-load the Turnstile script + render the widget when a site key is
  // configured. Idempotent — the script element is reused if it already
  // exists from a previous mount.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled) return;
      if (!turnstileMount.current || !window.turnstile) return;
      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileMount.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (tok) => setTurnstileToken(tok || ""),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
          theme: "light",
        });
      } catch {
        // Defensive: a double-mount in React StrictMode can throw if the
        // widget was already rendered into this node. Safe to ignore —
        // the previous widget is still functional.
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", renderWidget, { once: true });
      } else {
        const s = document.createElement("script");
        s.src = TURNSTILE_SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.addEventListener("load", renderWidget, { once: true });
        document.head.appendChild(s);
      }
    }
    return () => {
      cancelled = true;
      try {
        if (turnstileWidgetId.current && window.turnstile?.remove) {
          window.turnstile.remove(turnstileWidgetId.current);
        }
      } catch {
        // Cleanup is best-effort — if the widget already vanished there is
        // nothing meaningful to do here.
      }
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // When Turnstile is configured we require the token before submitting
    // — Cloudflare's widget normally auto-solves so users almost never see
    // a challenge.
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Please complete the captcha check.");
      return;
    }
    // Phone is mandatory for passenger (customer) sign-ups.
    if (form.role === "passenger" && form.phone.replace(/\D/g, "").length < 7) {
      toast.error("A valid phone number is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.role !== "driver") {
        delete payload.vehicle_make;
        delete payload.vehicle_plate;
      }
      if (turnstileToken) payload.turnstile_token = turnstileToken;
      const result = await register(payload);
      if (result?.kind === "verification_required") {
        // Production flow — no token yet. Redirect to the "check your
        // inbox" page so the user knows what to do next.
        toast.success("Account created — check your inbox to verify your email.");
        navigate(`/verify-email?pending=true&email=${encodeURIComponent(result.email)}`, { replace: true });
        return;
      }
      // Test-mode / legacy flow: a session was issued, go straight to dashboard.
      toast.success("Account created");
      navigate(redirectFor(result.user?.role || form.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="JOIN NETWORK" />
      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="label-mini text-muted-foreground mb-2">REGISTRATION</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Join the network</h1>
        </div>

        <form onSubmit={submit} className="space-y-5 bg-white border border-border p-6 md:p-8" data-testid="register-form">
          {/* Role tabs */}
          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            <button type="button" onClick={() => set("role", "passenger")}
              className={`py-3 label-mini transition-colors ${form.role === "passenger" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid="role-passenger-btn">
              PASSENGER
            </button>
            <button type="button" onClick={() => set("role", "driver")}
              className={`py-3 label-mini transition-colors ${form.role === "driver" ? "bg-[#FFC107] text-black" : "bg-white hover:bg-secondary"}`}
              data-testid="role-driver-btn">
              DRIVER
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="label-mini">FULL NAME</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="reg-name-input" />
            </div>
            <div>
              <Label className="label-mini">PHONE{form.role === "passenger" ? " *" : ""}</Label>
              <Input type="tel" required={form.role === "passenger"} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44 7700 900123" className="mt-1.5" data-testid="reg-phone-input" />
            </div>
            <div>
              <Label className="label-mini">EMAIL</Label>
              <Input type="email" required autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="reg-email-input" />
            </div>
            <div>
              <Label className="label-mini">PASSWORD</Label>
              <PasswordInput required minLength={4} autoComplete="new-password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="reg-password-input" />
            </div>
          </div>

          {form.role === "driver" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-5">
              <div>
                <Label className="label-mini">VEHICLE MAKE / MODEL</Label>
                <Input required value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} placeholder="Skoda Octavia" className="mt-1.5" data-testid="reg-vehicle-input" />
              </div>
              <div>
                <Label className="label-mini">LICENCE PLATE</Label>
                <Input required value={form.vehicle_plate} onChange={(e) => set("vehicle_plate", e.target.value)} placeholder="AB12 CDE" className="mt-1.5" data-testid="reg-plate-input" />
              </div>
            </div>
          )}

          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center" data-testid="register-turnstile-wrap">
              <div ref={turnstileMount} />
            </div>
          )}

          <Button type="submit" disabled={loading} size="lg" className="w-full" data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create account"}
          </Button>

          <div className="text-sm text-muted-foreground text-center">
            Already in?{" "}
            <Link to="/login" className="text-[#0A0A0A] font-semibold underline underline-offset-2" data-testid="link-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
